
import { DismissableToastComponent } from 'components/atoms/DismissableToastComponent/DismissableToastComponent';
import React from 'react';
import toast, { Toast, ToastOptions, type ToastPosition } from 'react-hot-toast';

interface IToastMessageProps {
  message: string
  duration?: number
  position?: ToastPosition
}

/**
 * Classe base abstrata para manipulação de notificações (toasts).
 * Fornece uma API com métodos específicos para cada tipo de notificação,
 * promovendo um código mais limpo e organizado.
 */
export abstract class ToastHelper {

  /**
   * Exibe uma notificação de sucesso (geralmente verde).
   * @param message A mensagem de texto a ser exibida.
   * @param options Opções de configuração do react-hot-toast (duração, posição, etc.).
   */
  public static success({ message, duration = 5000, position = 'top-center' }: IToastMessageProps): void {
    toast.success(message, {
      duration,
      position
    });
  }

  /**
   * Exibe uma notificação de erro (geralmente vermelha).
   * @param message A mensagem de texto a ser exibida.
   * @param options Opções de configuração do react-hot-toast.
   */
  public static error({ message, duration = 5000, position = 'top-center' }: IToastMessageProps): void {
    toast.error(message, {
      duration,
      position
    });
  }

  /**
   * Exibe uma notificação padrão (geralmente preta e branca).
   * Útil para informações gerais ou avisos.
   * @param message A mensagem a ser exibida (pode ser texto ou um componente React).
   * @param options Opções de configuração do react-hot-toast.
   */
  public static info({ message, duration = 5000, position = 'top-center' }: IToastMessageProps): void {
    toast(message, {
      duration,
      position
    });
  }

  /**
   * Exibe uma notificação customizada que inclui um botão para fechá-la.
   * @param message A mensagem a ser exibida (pode ser texto ou um componente React).
   * @param options Opções de configuração do react-hot-toast.
   */
  public static dismissable({ message, duration = 5000, position = 'top-center' }: IToastMessageProps): void {
    // O toast genérico é usado para renderizar um componente customizado.
    toast(
      (t: Toast) => (
        <DismissableToastComponent
          message={message}
          onClose={() => toast.dismiss(t.id)}
        />
      ),
      {
        duration,
        position
      }
    );
  }

  /**
   * Remove todos os toasts da tela.
   */
  public static dismissAll(): void {
    toast.dismiss();
  }
}